import { z } from "zod";
import { actionAvailabilitySchema, idSchema, instantSchema } from "@amber/contracts";
import { PASSAGE_CHECK_DIMENSIONS } from "./machine.js";

export const CHECK_ACTIONS = {
  request: "evidence.request_passage_check",
  optional: "evidence.edit_optional_check",
} as const;
export const EDITOR_CHECK_REASON = "Editors and above can request passage checks";
export const ADMIN_OPTIONAL_CHECK_REASON = "Only Admins can change optional checks";

export const CHECK_DIMENSION_LABELS: Record<(typeof PASSAGE_CHECK_DIMENSIONS)[number], string> = {
  wording: "Wording",
  locator: "Position",
  context: "Context",
};

export const passageCheckBodySchema = z.strictObject({
  unit_ids: z.array(idSchema("unt")).min(1).max(20),
  dimensions: z.array(z.enum(PASSAGE_CHECK_DIMENSIONS)).min(1).max(3),
  manual_review: z.boolean().default(false),
  reason: z.string().trim().min(1).max(500),
});

export const CHECK_MARKS = ["verified", "failed", "rechecking", "unverified"] as const;
export const CHECK_OUTCOMES = ["passed", "failed", "insufficient", "not_run"] as const;

export const turnCheckSchema = z.strictObject({
  id: idSchema("chk"),
  mark: z.enum(CHECK_MARKS),
  label: z.string(),
  range_label: z.string(),
  dimensions: z.array(
    z.strictObject({
      dimension: z.enum(PASSAGE_CHECK_DIMENSIONS),
      label: z.string(),
      outcome: z.enum(CHECK_OUTCOMES),
      detail: z.string(),
    }),
  ),
  other: z.array(
    z.strictObject({ name: z.string(), state: z.enum(["not_run", "not_applicable", "recorded"]), detail: z.string() }),
  ),
  requested_at: instantSchema,
  requested_by: z.string(),
  completed_at: instantSchema.nullable(),
  against: z.string(),
  method: z.string(),
  next: z.string().nullable(),
  failure_detail: z.string().nullable(),
  task_href: z.string().nullable(),
});
export type TurnCheck = z.infer<typeof turnCheckSchema>;

export const checkOptionsSchema = z.strictObject({
  manual_review: actionAvailabilitySchema,
  estimate_usd: z.string(),
  suspended: z.string().nullable(),
});

export const passageCheckResultSchema = z.strictObject({
  id: idSchema("chk"),
  state: z.enum(["requested", "running", "completed", "failed_to_run"]),
  reused: z.boolean(),
  task_id: z.string().nullable(),
});

export const OPTIONAL_CHECK_KINDS = ["semantic_speaker", "manual_speaker", "manual_passage"] as const;
export type OptionalCheckKind = (typeof OPTIONAL_CHECK_KINDS)[number];

export const optionalCheckScopeSchema = z.strictObject({
  below: z.number().min(0.05).max(1),
  platforms: z.array(z.string().min(1)).min(1),
  languages: z.array(z.enum(["en", "de"])).min(1),
  sample_percent: z.number().int().min(1).max(100),
});
export type OptionalCheckScope = z.infer<typeof optionalCheckScopeSchema>;

export const optionalCheckBoundsSchema = z.strictObject({
  monthly_cost_usd: z.number().min(0).max(100_000),
  max_delay_hours: z.number().int().min(0).max(720),
});

export const optionalCheckViewSchema = z.strictObject({
  kind: z.enum(OPTIONAL_CHECK_KINDS),
  name: z.string(),
  performed_by: z.enum(["Automated", "Person"]),
  description: z.string(),
  enabled: z.boolean(),
  scope: optionalCheckScopeSchema,
  bounds: optionalCheckBoundsSchema,
  revision: z.number().int().positive(),
  changed: z.strictObject({ by: z.string(), at: instantSchema, reason: z.string() }).nullable(),
  this_month: z.strictObject({
    checked: z.number().int(),
    skipped: z.number().int(),
    cost_usd: z.string(),
    outcomes: z.record(z.string(), z.number().int()),
  }),
});
export type OptionalCheckView = z.infer<typeof optionalCheckViewSchema>;

export const optionalChecksResponseSchema = z.strictObject({
  checks: z.array(optionalCheckViewSchema),
  platforms: z.array(z.strictObject({ id: z.string(), label: z.string() })),
  actions: z.strictObject({ edit: actionAvailabilitySchema }),
});
export type OptionalChecksResponse = z.infer<typeof optionalChecksResponseSchema>;

export const optionalCheckEditSchema = z.strictObject({
  enabled: z.boolean(),
  scope: optionalCheckScopeSchema,
  bounds: optionalCheckBoundsSchema,
  base_revision: z.number().int().positive(),
  reason: z.string().trim().min(1).max(500),
});
export type OptionalCheckEdit = z.infer<typeof optionalCheckEditSchema>;

export const optionalCheckPreviewSchema = z.strictObject({
  per_week: z.number().int().nonnegative(),
  monthly_cost_usd: z.string().nullable(),
  person_hours_per_week: z.number().nullable(),
  over_cap: z.boolean(),
  basis: z.string(),
});
export type OptionalCheckPreview = z.infer<typeof optionalCheckPreviewSchema>;
